import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { CompListing, Listing, Seating, SplitType } from 'src/WebApiController';

export type ListingWithValidPurchaseQuantities = Listing &
  Pick<CompListing, 'validPurchaseQuantities'>;

export const mapCompListingToListing = (
  listing: CompListing
): ListingWithValidPurchaseQuantities => {
  return {
    id: listing.listingId,
    availQty: listing.availableTickets ?? undefined,
    unsoldQty: listing.availableTickets ?? undefined,
    seating: {
      section: listing.section ?? '',
      row: listing.row,
      rowId: listing.rowId,
      seatFr: listing.seatFrom,
      seatTo: listing.seatTo,
    } as Seating,
    allInPrice: listing.sellerAllInPrice?.amt ?? undefined,
    currency:
      listing.sellerAllInPrice?.currency ?? listing.currencyCode ?? 'USD',
    validPurchaseQuantities: listing.validPurchaseQuantities,
  } as ListingWithValidPurchaseQuantities;
};

export const prependTicketClassColorColumnForFirstTime = (
  storedInventoryColumnOrderSetting: string[],
  storedInventoryColumnsEnabledSetting: string[],
  setInventoryColumnOrderSetting: (value: string[]) => void,
  setInventoryColumnsEnabledSetting: (value: string[]) => void
) => {
  // Only add if it's not already in the order setting list - meaning the user never configured it
  if (
    !storedInventoryColumnOrderSetting.includes(
      ListingTableColumnId.TicketClassColor
    )
  ) {
    // In this case we turn it on and add it to the beginning of the order setting
    setInventoryColumnOrderSetting([
      ListingTableColumnId.TicketClassColor,
      ...storedInventoryColumnOrderSetting,
    ]);
    if (
      !storedInventoryColumnsEnabledSetting.includes(
        ListingTableColumnId.TicketClassColor
      )
    ) {
      setInventoryColumnsEnabledSetting([
        ListingTableColumnId.TicketClassColor,
        ...storedInventoryColumnsEnabledSetting,
      ]);
    }
  }
};

/**
 * Helper function to compute the valid purchase quantities from
 * [listing available quantity, split type and max display quantity]
 *
 * Logic is cloned from
 * https://github.com/stubhub/Viagogo.Listings/blob/master/src/Viagogo.Listings/Extensions/IBuyerListingExtensions.cs#L14
 * @returns valid purchase quantities
 */
export const getValidPurchaseQuantities = (listing: Listing): number[] => {
  const availQty = listing.availQty;
  let { splitType, maxDispQty } = listing;
  const results: number[] = [];

  if (maxDispQty < 2) {
    splitType = SplitType.Any;
  }
  if (maxDispQty <= 0) {
    maxDispQty = 999;
  }

  switch (splitType) {
    case SplitType.Any:
      for (let i = 1; i <= availQty && i <= maxDispQty; i++) {
        results.push(i);
      }
      break;

    case SplitType.None:
      if (availQty <= maxDispQty) {
        results.push(availQty);
      } else {
        results.push(maxDispQty);
      }
      break;

    case SplitType.AvoidOne:
      for (let i = 1; i <= availQty; i++) {
        if (i !== availQty - 1 && i <= maxDispQty) {
          results.push(i);
        }
      }
      break;

    case SplitType.AvoidOneAndThree:
      for (let i = 1; i <= availQty; i++) {
        if (i !== availQty - 1 && i !== availQty - 3 && i <= maxDispQty) {
          results.push(i);
        }
      }
      break;

    case SplitType.Pairs:
      if (availQty % 2 !== 0) {
        for (let i = 1; i <= availQty && i <= maxDispQty; i++) {
          results.push(i);
        }
      } else {
        for (let i = 2; i <= availQty && i <= maxDispQty; i += 2) {
          results.push(i);
        }
      }
      break;
  }

  return results;
};
